import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid-premium";
import CustomTable from "components/shared/table";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import * as LocalStorageKeys from "data/local-storage-keys";
import { fetchClipsPaginated } from "data/queries";
import { Settings2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { resolveIdentityId } from "utils/identity";
import { useLocalStorage } from "utils/local-storage";
import { ClipConfiguration, Status } from "../clip-ui/models";
import { ClipDetailSidebar } from "./clip-detail-sidebar";
import { getClipManagementColumns } from "./columns";
import { ColumnVisibilityMenu, RowActionsMenu } from "./menus";

const NON_EDITABLE_STATUSES: Status[] = ["Completed", "Failed"];

export const ClipManagementPage = () => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery('(min-width:1440px)');
  const { enqueueInfoSnackbar } = useCustomSnackbars();

  const [selectedClip, setSelectedClip] = useState<ClipConfiguration | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [authorFilter, setAuthorFilter] = useState<string[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClipForMenu, setSelectedClipForMenu] =
    useState<ClipConfiguration | null>(null);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  // Column visibility state synced with localStorage
  const [columnVisibility, setColumnVisibility] = useLocalStorage<
    Record<string, boolean>
  >(LocalStorageKeys.clipManagementColumnVisibility, {
    name: true,
    premium: true,
    clientReference: true,
    totalCredits: true,
    totalDollars: true,
    status: true,
    author: true,
    lastEditedAt: true,
    actions: true,
  });

  const clipsQuery = useInfiniteQuery(
    [QueryKeys.clipsKey, identityId, "management", "withCredits"],
    ({ pageParam }) => {
      const limit = 50;
      return fetchClipsPaginated(pageParam, limit, true); // Pass include_credits=true
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.hasMore
          ? JSON.stringify(lastPage.lastEvaluatedKey)
          : undefined,
      refetchInterval: 30000,
    }
  );

  // Flatten all pages into a single array
  const allClips = useMemo(() => {
    return clipsQuery.data?.pages.flatMap((page) => page.clips) ?? [];
  }, [clipsQuery.data]);

  // Automatically fetch all pages in the background
  useEffect(() => {
    if (
      clipsQuery.hasNextPage &&
      !clipsQuery.isFetchingNextPage &&
      !clipsQuery.isLoading
    ) {
      clipsQuery.fetchNextPage();
    }
  }, [
    clipsQuery.hasNextPage,
    clipsQuery.isFetchingNextPage,
    clipsQuery.isLoading,
  ]);

  // Get unique statuses and authors for filters
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(allClips.map((clip) => clip.status));
    return Array.from(statuses);
  }, [allClips]);

  const uniqueAuthors = useMemo(() => {
    const authors = new Set(
      allClips
        .map((clip) => clip.authorName || clip.developer || "Unknown")
        .filter(Boolean)
    );
    return Array.from(authors);
  }, [allClips]);

  // Filter clips based on search and filters
  const filteredClips = useMemo(() => {
    return allClips.filter((clip) => {
      const matchesSearch =
        clip.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        clip.clientReference?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter.length === 0 || statusFilter.includes(clip.status);

      const clipAuthor = clip.authorName || clip.developer || "Unknown";
      const matchesAuthor =
        authorFilter.length === 0 || authorFilter.includes(clipAuthor);

      return matchesSearch && matchesStatus && matchesAuthor;
    });
  }, [allClips, searchQuery, statusFilter, authorFilter]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    clip: ClipConfiguration
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedClipForMenu(clip);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedClipForMenu(null);
  };

  const handleRowClick = (params: GridRowParams) => {
    if (!isLargeScreen) {
      enqueueInfoSnackbar("Increase screen size to view clip details", "info");
      return;
    }
    setSelectedClip(params.row as ClipConfiguration);
  };

  const handleColumnToggle = (column: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const columns = getClipManagementColumns({ handleMenuOpen });

  const rows = filteredClips.map((clip) => ({
    ...clip,
    author: clip.authorName || clip.developer || "",
  }));

  return (
    <Box display="flex" gap={2} alignItems="flex-start">
      {/* Left side - Table */}
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        <Box
          display="flex"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" gap={2} flex={1} minWidth={0}>
            <TextField
              size="small"
              label="Search"
              placeholder="Search by name or client ref..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: selectedClip && isLargeScreen ? undefined : 180,
                minWidth: selectedClip && isLargeScreen ? 120 : undefined,
                flexShrink: selectedClip && isLargeScreen ? 1 : 0,
              }}
            />
            <FormControl
              sx={{
                width: selectedClip && isLargeScreen ? undefined : 180,
                minWidth: selectedClip && isLargeScreen ? 120 : undefined,
                flexShrink: selectedClip && isLargeScreen ? 1 : 0,
              }}
              size="small"
            >
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter-select"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as string[])}
                renderValue={(selected) =>
                  selected.length === 0 ? "All" : selected.join(", ")
                }
                multiple
              >
                {uniqueStatuses.map((status) => (
                  <MenuItem
                    key={status}
                    value={status}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log("Toggling status:", status);
                      console.log("Current statusFilter:", statusFilter);
                      setStatusFilter((prev) => {
                        const newFilter = prev.includes(status)
                          ? prev.filter((s) => s !== status)
                          : [...prev, status];
                        console.log("New statusFilter:", newFilter);
                        return newFilter;
                      });
                    }}
                  >
                    <Checkbox checked={statusFilter.includes(status)} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{
                width: selectedClip && isLargeScreen ? undefined : 180,
                minWidth: selectedClip && isLargeScreen ? 120 : undefined,
                flexShrink: selectedClip && isLargeScreen ? 1 : 0,
              }}
              size="small"
            >
              <InputLabel id="author-filter-label">Author</InputLabel>
              <Select
                labelId="author-filter-label"
                id="author-filter-select"
                label="Author"
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value as string[])}
                renderValue={(selected) =>
                  selected.length === 0 ? "All" : selected.join(", ")
                }
                multiple
              >
                {uniqueAuthors.map((author) => (
                  <MenuItem
                    key={author}
                    value={author}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAuthorFilter((prev) =>
                        prev.includes(author)
                          ? prev.filter((a) => a !== author)
                          : [...prev, author]
                      );
                    }}
                  >
                    <Checkbox checked={authorFilter.includes(author)} />
                    <ListItemText primary={author} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(searchQuery !== "" ||
              statusFilter.length > 0 ||
              authorFilter.length > 0) && (
              <Button
                variant="text"
                startIcon={<X size={16} />}
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter([]);
                  setAuthorFilter([]);
                }}
                sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
              >
                Clear
              </Button>
            )}
          </Box>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Settings2 size={16} />}
            onClick={(e) => setColumnsMenuAnchorEl(e.currentTarget)}
            sx={{ flexShrink: 0 }}
          >
            Columns
          </Button>
        </Box>

        <CustomTable
          rows={rows}
          cols={columns}
          isLoading={clipsQuery.isLoading}
          overlayText="No clips found"
          handleOnRowClick={handleRowClick}
          getRowId={(row: { id: any }) => row.id}
          disableRowSelectionOnClick={false}
          disableColumnMenu={true}
          density="standard"
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
            pinnedColumns: {
              right: ["actions"],
            },
          }}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={setColumnVisibility}
          customStyle={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
          }}
        />
      </Box>

      {/* Right side - Detail Sidebar */}
      {selectedClip && isLargeScreen && (
        <ClipDetailSidebar
          clip={selectedClip}
          onClose={() => setSelectedClip(null)}
        />
      )}

      {/* Context Menu */}
      <RowActionsMenu
        anchorEl={menuAnchorEl}
        selectedClip={selectedClipForMenu}
        nonEditableStatuses={NON_EDITABLE_STATUSES}
        onClose={handleMenuClose}
      />

      {/* Columns Visibility Menu */}
      <ColumnVisibilityMenu
        anchorEl={columnsMenuAnchorEl}
        columnVisibility={columnVisibility}
        onClose={() => setColumnsMenuAnchorEl(null)}
        onToggle={handleColumnToggle}
      />
    </Box>
  );
};
